<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import InputText from 'primevue/inputtext';
import Skeleton from 'primevue/skeleton';
import { useToast } from 'primevue/usetoast';
import { PersonStores, usePeopleTypesStore } from '@/store';
import { can, usePersonTypes } from '@/composables';
import { PERMISSIONS } from '@/enum';
import { ExternalAssociationInterface } from '@/types';
import InfoTooltip from '@/components/InfoTooltip';

const profileStore = PersonStores.useUserProfileStore();
const toast = useToast();
const externalAssociation = ref({} as ExternalAssociationInterface);
const $externalResults = ref({});
const ssisColabUserId = ref<string>('');
const originalSsisColabUserId = ref<string>('');
const elumaStudentId = ref<string>('');
const PEOPLE_TYPES = usePeopleTypesStore().PEOPLE_TYPES;
const errors = ref(null);

const rules = computed(() => {
  return {
    external_id: {
      required,
      minLengthValue: minLength(2),
    },
  };
});

const ssisRules = {
  ssisColabUserId: {},
};

const v$Ssis = useVuelidate(ssisRules, { ssisColabUserId });

const isStudent = computed(() => usePersonTypes.isStudent(profileStore.person?.people_type?.acronym));

const v$ = useVuelidate(rules, externalAssociation, { $externalResults });

/**
 * Initialize everything required to build out the component.
 */
onMounted(() => {
  setExternalAssociation();
});

/**
 * Set the original externalAssociation
 */
const setExternalAssociation = () => {
  if (profileStore.person) {
    externalAssociation.value = clonedExternalAssociation();
    ssisColabUserId.value = profileStore.person.ssis_colab_user_id || '';
    originalSsisColabUserId.value = profileStore.person.ssis_colab_user_id || '';
    elumaStudentId.value = profileStore.person.eluma_student_id || '';
  }
};

/**
 * Return a cloned version of the external association
 */
const clonedExternalAssociation = () => {
  return { ...profileStore.person.external_association };
};

/**
 * Define the request update payload.
 */
const payload = computed(() => {
  const value = externalAssociation.value;

  if (isStudent.value) {
    return {
      external_id: value.external_id,
      organization_id: !value.organization_id ? profileStore.person?.organizations?.[0] : value.organization_id,
    };
  }

  return {
    external_id: value.external_id,
    organization_id: value.organization_id,
  };
});

watch(() => profileStore.person, setExternalAssociation);

/**
 * Check if SSIS CoLab ID has changed
 */
const hasSsisColabChanged = () => ssisColabUserId.value !== originalSsisColabUserId.value;

/**
 * Check if External ID has changed
 */
const hasExternalIdChanged = () => v$.value.$anyDirty && externalAssociation.value.external_id;

/**
 * Update SSIS CoLab ID only
 */
const updateSsisColabOnly = async () => {
  const ssisPayload = {
    ssis_colab_user_id: ssisColabUserId.value || null,
  };

  try {
    await profileStore.updatePersonField(ssisPayload);
    originalSsisColabUserId.value = ssisColabUserId.value;
    profileStore.person.ssis_colab_user_id = ssisColabUserId.value;
    toast.add({
      severity: 'success',
      summary: 'Saved',
      detail: 'SSIS CoLab ID has been updated',
      life: 3000,
    });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update SSIS CoLab ID',
      life: 3000,
    });
  }
};

/**
 * Validate external ID update for students
 */
const validateStudentExternalId = () => {
  if (isStudent.value && !payload.value.organization_id) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'The Student must be connected to an Organization prior to assigning an SSID',
      life: 3000,
    });
    return false;
  }
  return true;
};

/**
 * Update External ID with optional SSIS CoLab
 */
const updateExternalIdWithOptionalSsis = async (includeSSIS: boolean) => {
  if (!(await v$.value.$validate())) return;

  if (!validateStudentExternalId()) return;

  const updatePayload = { ...payload.value };
  if (includeSSIS) {
    updatePayload.ssis_colab_user_id = ssisColabUserId.value || null;
  }

  try {
    const result = await profileStore.updateExternalId(updatePayload);
    handleResult(result);
    doSuccess();
    if (includeSSIS) {
      originalSsisColabUserId.value = ssisColabUserId.value;
      profileStore.person.ssis_colab_user_id = ssisColabUserId.value;
    }
  } catch (response) {
    doFail(response);
  }
};

/**
 * on save external_id, reset dirty and push to API
 */
const onSave = async () => {
  if (!profileStore.person) return;

  const ssisChanged = hasSsisColabChanged();
  const externalIdChanged = hasExternalIdChanged();

  // Handle SSIS CoLab update only
  if (ssisChanged && !externalIdChanged) {
    await updateSsisColabOnly();
    return;
  }

  // Handle External ID update (with or without SSIS CoLab)
  if (externalIdChanged) {
    await updateExternalIdWithOptionalSsis(ssisChanged);
  }
  resetDirty();
};

/**
 * reset the form dirty settting
 * which hides the footer buttons and style
 */
const resetDirty = () => {
  v$.value.$reset();
  v$.value.$clearExternalResults();
};

/**
 * on cancel reset the dirty and reset externalID
 */
const onCancel = () => {
  setExternalAssociation();
  resetDirty();
  v$Ssis.value.$reset();
};

/**
 * Display the toast message.
 */
const doToast = (error = false) => {
  toast.add({
    severity: error ? 'error' : 'success',
    summary: error ? 'Error' : 'Saved',
    detail: error ? 'there was an error' : 'External Association has been saved',
    life: 3000,
  });
};

const doSuccess = () => {
  if (!profileStore.person.external_association) {
    profileStore.person.external_association = {} as ExternalAssociationInterface;
  }
  profileStore.person.external_association.external_id = externalAssociation.value.external_id;
  onCancel();
};

const doFail = (response) => {
  if (response.data.errors.external_id) {
    $externalResults.value = {
      external_id: response.data.errors.external_id,
    };
  }
};

/**
 * handle the update results
 */
const handleResult = (result: number | undefined) => {
  if (result === 200) {
    doToast();
  } else {
    doToast(true);
  }
};

const isDirty = computed(() => {
  return v$.value.$anyDirty || ssisColabUserId.value !== originalSsisColabUserId.value;
});

const canUpdateExternalAssociations = () => {
  const personType: string = profileStore.person?.people_type?.acronym as string;
  let permissionString = 'PEOPLE_UPDATE_EXTERNAL_ASSOCIATIONS';
  let str: keyof typeof PERMISSIONS;
  switch (personType) {
    case PEOPLE_TYPES.SUPPORT_ADMIN: {
      permissionString += '_SUPPORT_ADMIN';
      break;
    }
    case PEOPLE_TYPES.SCHOOL_ADMIN: {
      permissionString += '_SCHOOL_ADMIN';
      break;
    }
    case PEOPLE_TYPES.SUPER_ADMIN: {
      permissionString += '_SUPER_ADMIN';
      break;
    }
  }
  str = permissionString as keyof typeof PERMISSIONS;

  return can([PERMISSIONS[str]]);
};
</script>

<template>
  <FormCard
    title="External Associations"
    :dirty="isDirty"
    :cancel="onCancel"
    :save="onSave"
    :save-disabled="!canUpdateExternalAssociations() || (v$.$invalid && v$.$dirty)"
  >
    <template #form>
      <form class="w-full">
        <div class="formgrid grid">
          <div class="field col-12">
            <div class="flex flex-wrap align-items-center mb-0 gap-1">
              <label for="email">{{ isStudent ? 'SSID' : 'External ID' }}</label>
              <InfoTooltip v-if="isStudent" class="cursor-pointer">
                <span>
                  Enter SSID (Statewide Student Identifier). If that is unknown please use District/School ID)
                </span>
              </InfoTooltip>
            </div>
            <Skeleton v-if="profileStore.loading" width="100%" height="2.3rem" />
            <InputText
              v-else
              id="external_id"
              v-model="v$.external_id.$model"
              :class="{ 'p-invalid': v$.$invalid && v$.$dirty }"
              :disabled="!canUpdateExternalAssociations()"
              @input="v$.$touch"
            />
            <small v-if="errors && errors.external_id" class="p-error">{{ errors.external_id[0] }}</small>
            <small v-for="error of v$.external_id.$externalResults" :key="error.$uid" class="p-error">
              {{ error.$message }}
            </small>
          </div>
          <div class="field col-12">
            <label for="ssis_colab">SSIS CoLab</label>
            <Skeleton v-if="profileStore.loading" width="100%" height="2.3rem" />
            <InputText
              v-else
              id="ssis_colab"
              v-model="ssisColabUserId"
              placeholder="Enter alphanumeric ID"
              :disabled="!canUpdateExternalAssociations()"
              @input="v$Ssis.$touch"
            />
          </div>
          <div v-if="isStudent && can([PERMISSIONS.STUDENT_VIEW_ELUMA_STUDENT_ID])" class="field col-12">
            <label for="eluma_student_id">Eluma Student ID</label>
            <Skeleton v-if="profileStore.loading" width="100%" height="2.3rem" />
            <InputText
              v-else
              id="eluma_student_id"
              v-model="elumaStudentId"
              placeholder="Eluma Student ID"
              :disabled="true"
            />
          </div>
        </div>
      </form>
    </template>
  </FormCard>
</template>
